import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table from '../components/Table';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const fetchLeaveTypeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/lms/leave-types');
  const types = res.data.leaveTypes || [];
  const filtered = search ? types.filter((t: any) => t.name.toLowerCase().includes(search.toLowerCase())) : types;
  return filtered.map((t: any) => ({ value: t.id, label: `${t.name} (${t.code})` }));
};

export default function LeaveAdjustments() {
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [mode, setMode] = useState<'INDIVIDUAL' | 'BATCH'>('INDIVIDUAL');
  const [employeeId, setEmployeeId] = useState('');
  const [batchEmployeeIds, setBatchEmployeeIds] = useState<string[]>([]);
  const [batchEmployeeNames, setBatchEmployeeNames] = useState<Record<string, string>>({});
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [form, setForm] = useState({ adjustmentType: 'ADD', adjustmentDays: 1, reason: '', effectiveDate: '' });

  const load = async () => {
    const res = await api.get('/lms/leave-adjustments');
    setAdjustments(res.data.adjustments || []);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!leaveTypeId) return alert('Select a leave type');
    try {
      if (mode === 'INDIVIDUAL') {
        if (!employeeId) return alert('Select an employee');
        await api.post('/lms/leave-adjustments', { employeeId, leaveTypeId, ...form });
      } else {
        if (!batchEmployeeIds.length) return alert('Add at least one employee');
        await api.post('/lms/leave-adjustments/batch', { employeeIds: batchEmployeeIds, leaveTypeId, ...form });
      }
      setForm({ adjustmentType: 'ADD', adjustmentDays: 1, reason: '', effectiveDate: '' });
      setEmployeeId('');
      setBatchEmployeeIds([]);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Credit Annual Leave</h1>
      <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
        <div className="flex gap-4">
          <button onClick={() => setMode('INDIVIDUAL')} className={`px-3 py-1 rounded text-sm ${mode === 'INDIVIDUAL' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>Individual</button>
          <button onClick={() => setMode('BATCH')} className={`px-3 py-1 rounded text-sm ${mode === 'BATCH' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>Batch</button>
        </div>
        {mode === 'INDIVIDUAL' ? (
          <LookupSelect label="Employee" value={employeeId} onChange={setEmployeeId} fetchOptions={fetchEmployeeOptions} />
        ) : (
          <div>
            <label className="text-sm font-medium text-gray-700">Employees</label>
            <LookupSelect
              value=""
              placeholder="Add employee..."
              fetchOptions={fetchEmployeeOptions}
              onChange={(v, opt) => {
                if (!v || batchEmployeeIds.includes(v)) return;
                setBatchEmployeeIds([...batchEmployeeIds, v]);
                if (opt) setBatchEmployeeNames({ ...batchEmployeeNames, [v]: opt.label });
              }}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {batchEmployeeIds.map((id) => (
                <span key={id} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex items-center gap-1">
                  {batchEmployeeNames[id] || id}
                  <button onClick={() => setBatchEmployeeIds(batchEmployeeIds.filter((e) => e !== id))} title="Remove">&times;</button>
                </span>
              ))}
            </div>
          </div>
        )}
        <LookupSelect label="Leave Type" value={leaveTypeId} onChange={setLeaveTypeId} fetchOptions={fetchLeaveTypeOptions} />
        <div className="grid grid-cols-3 gap-4">
          <Select label="Type" value={form.adjustmentType} onChange={(e) => setForm({ ...form, adjustmentType: e.target.value })}
            options={[{ value: 'ADD', label: 'Add' }, { value: 'DEDUCT', label: 'Deduct' }]} />
          <Input label="Days" type="number" step="0.5" value={form.adjustmentDays} onChange={(e) => setForm({ ...form, adjustmentDays: Number(e.target.value) })} />
          <Input label="Effective Date" type="date" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
          <Input label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="col-span-3" />
        </div>
        <Button onClick={submit}>Save</Button>
      </div>

      <Table
        columns={[
          { key: 'employee', header: 'Employee', render: (r: any) => `${r.first_name} ${r.last_name}` },
          { key: 'leave_type_name', header: 'Leave Type' },
          { key: 'adjustment_type', header: 'Type' },
          { key: 'adjustment_days', header: 'Days' },
          { key: 'effective_date', header: 'Effective Date', render: (r: any) => (r.effective_date ? new Date(r.effective_date).toLocaleDateString() : '—') },
        ]}
        rows={adjustments}
      />
    </div>
  );
}
